// StageProgress: the 5-stage production pipeline as compact steps (DESIGN §2, deliverables per §7).
// ✓ done (positive) · lime dot = current · gray dot = future · red = blocked · amber = review.
// variant 'mini' (table rows: 5 pips, the current one filled by its partial count, optional "Script · 12/15" label) |
// 'full' (batch detail: numbered steps + labels + per-stage "12/15"). Also the deliverable helpers the batch views use.
import './stage-progress.css';
import { BRIEFS, STAGES, stageTarget } from '../../shared/constants.js';
import { Icon } from '../icons.jsx';
import { cx, statusLabel } from './ui.jsx';

export { BRIEFS, stageTarget };
export const pipelineStage = (key) => STAGES.find((s) => s.key === key);

const isDone = (batch, key) => batch?.stageStatus?.[key] === 'Done';
const isBriefs = (key) => pipelineStage(key)?.target === 'briefs';
const hasCounter = (key) => pipelineStage(key)?.target !== 'one';

// Stage 03 brief ticked (a Done stage 03 has both).
export function briefDone(batch, key) {
  return isDone(batch, 'videoShootEditingBrief') || !!batch?.briefsDone?.[key];
}

// Finished deliverables of a stage (a Done stage is always full).
export function stageCount(batch, key) {
  const target = stageTarget(batch, key);
  if (isDone(batch, key)) return target;
  if (isBriefs(key)) return BRIEFS.filter((b) => briefDone(batch, b.key)).length;
  const n = Math.round(Number(batch?.progress?.[key]) || 0);
  return Math.min(target, Math.max(0, n));
}

// "12/15" (stages 02–05); '' for the single-deliverable Brief Script.
export function stageCounter(batch, key) {
  return hasCounter(key) ? `${stageCount(batch, key)}/${stageTarget(batch, key)}` : '';
}

const notStarted = (s) => !s || s === 'To Do' || s === 'Not Started';
const inProduction = (batch) => !!batch && batch.status !== 'Done' && !notStarted(batch.status);

// Step state of every stage: 'done' | 'current' | 'review' | 'blocked' | 'future'.
export function stageSteps(batch) {
  const status = batch?.stageStatus || {};
  const current = inProduction(batch) ? batch.currentStage : null;
  return STAGES.map((stage) => {
    const s = status[stage.key];
    let state = 'future';
    if (s === 'Done') state = 'done';
    else if (s === 'Blocked') state = 'blocked';
    else if (s === 'Review') state = 'review';
    else if (s === 'In Progress' || stage.key === current) state = 'current';
    const target = stageTarget(batch, stage.key);
    const count = stageCount(batch, stage.key);
    return { stage, status: statusLabel(s || 'To Do'), state, isCurrent: stage.key === current, count, target, counter: stageCounter(batch, stage.key) };
  });
}

// "Script · 12/15" for batches in production, "Not started" / "Complete" otherwise. short: "Editing · 3/15".
export function stageText(batch, { short = false, counter = true } = {}) {
  if (!batch) return '';
  if (batch.status === 'Done') return 'Complete';
  if (!inProduction(batch)) return 'Not started';
  const stage = pipelineStage(batch.currentStage);
  if (!stage) return '';
  const c = counter ? stageCounter(batch, stage.key) : '';
  // Stage 03's full name is too long for a row: it reads "Briefs · 1/2" everywhere outside the batch page.
  return [short || stage.target === 'briefs' ? stage.short : stage.label, c].filter(Boolean).join(' · ');
}

// Current stage label; a table on a narrow main column swaps in the short name ("Editing · 3/15") via CSS.
function StageLabel({ batch }) {
  const text = stageText(batch);
  const short = inProduction(batch) ? stageText(batch, { short: true }) : '';
  if (!short || short === text) return <span class="sp-label">{text}</span>;
  return (
    <span class="sp-label" title={text}>
      <span class="sp-long">{text}</span>
      <span class="sp-short">{short}</span>
    </span>
  );
}

function Mark({ state }) {
  if (state === 'done') return <Icon name="check" size={13} />;
  if (state === 'blocked') return <span class="sp-square" />;
  return <span class="sp-dot" />;
}

// batch: needs stageStatus, currentStage, status, progress/briefsDone/videoCount (v3; older data reads as 0).
// onSelect(stageKey): full variant only — each step becomes a button (e.g. scroll to that stage row).
export function StageProgress({ batch, variant = 'mini', showLabel = false, onSelect, class: cls }) {
  const steps = stageSteps(batch);
  if (variant === 'full') {
    return (
      <ol class={cx('sp-full', cls)} aria-label="Production stages">
        {steps.map(({ stage, state, status, counter, isCurrent }) => {
          const Tag = onSelect ? 'button' : 'span';
          const sub = counter || status;
          return (
            <li key={stage.key} class={cx('sp-step', `is-${state}`)} aria-current={isCurrent ? 'step' : undefined}>
              <Tag
                class="sp-step-inner"
                type={onSelect ? 'button' : undefined}
                onClick={onSelect ? () => onSelect(stage.key) : undefined}
                aria-label={`${stage.number} ${stage.label}: ${status}${counter ? `, ${counter}` : ''}`}
              >
                <span class="sp-mark" aria-hidden="true"><Mark state={state} /></span>
                <span class="sp-text" aria-hidden="true">
                  <span class="sp-num">{stage.number}</span>
                  <span class="sp-name">{stage.short === 'Briefs' ? 'Briefs' : stage.label}</span>
                  <span class={cx('sp-status', counter && 'num')}>{sub}</span>
                </span>
              </Tag>
            </li>
          );
        })}
      </ol>
    );
  }
  const text = stageText(batch);
  return (
    <span class={cx('sp-mini', cls)} role="img" aria-label={`Stage: ${text}`}>
      <span class="sp-track" aria-hidden="true">
        {steps.map(({ stage, state, isCurrent, count, target }) => (
          <span
            key={stage.key}
            class={cx('sp-pip', `is-${state}`, isCurrent && 'is-now', isCurrent && count > 0 && count < target && 'is-partial')}
            style={isCurrent && count > 0 && count < target ? { '--sp-p': `${Math.round((count / target) * 100)}%` } : undefined}
          />
        ))}
      </span>
      {showLabel && <StageLabel batch={batch} />}
    </span>
  );
}
